<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import NucleoWorkOrderConfirmationDialog from './NucleoWorkOrderConfirmationDialog.vue';
import NucleoWorkOrderServiceExecutionForm from './NucleoWorkOrderServiceExecutionForm.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  service: { type: Object, default: null },
  saving: { type: Boolean, default: false },
  transactionId: { type: [String, Number], default: null },
  parties: { type: Array, default: () => [] },
});
const emit = defineEmits(['close', 'save']);
const { t } = useI18n();
const confirming = ref(false);
const form = ref({
  mode: 'update',
  scheduled_for: '',
  observacao: '',
  files: [],
  document_artifact_ids: [],
});
const canUpdate = computed(() => props.service?.actions?.includes('schedule'));
const canComplete = computed(() =>
  props.service?.actions?.includes('complete')
);
const valid = computed(
  () => form.value.mode === 'complete' || Boolean(form.value.scheduled_for)
);
const title = computed(() =>
  t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.TITLE', {
    service: props.service?.nome || '',
  })
);

watch(
  () => props.show,
  visible => {
    if (!visible) return;
    confirming.value = false;
    form.value = {
      mode: canUpdate.value ? 'update' : 'complete',
      scheduled_for: '',
      observacao: '',
      files: [],
      document_artifact_ids: [],
    };
  },
  { immediate: true }
);

const updateForm = changes => {
  form.value = { ...form.value, ...changes };
};
const selectFiles = event =>
  updateForm({ files: Array.from(event.target.files || []) });
const addDocuments = documentIds =>
  updateForm({
    document_artifact_ids: [
      ...new Set([...form.value.document_artifact_ids, ...documentIds]),
    ],
  });
const submit = () => emit('save', { ...form.value });
</script>

<template>
  <LaisModal
    v-if="!confirming"
    :show="show"
    :title="title"
    max-width-class="max-w-[620px]"
    @close="emit('close')"
  >
    <NucleoWorkOrderServiceExecutionForm
      v-if="!confirming"
      :form="form"
      :can-update="canUpdate"
      :can-complete="canComplete"
      :transaction-id="transactionId"
      :parties="parties"
      @change="updateForm"
      @files-selected="selectFiles"
      @documents-uploaded="addDocuments"
    />
    <template #footer>
      <div class="flex justify-end gap-3">
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          {{ t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.BACK') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          :disabled="!valid"
          :is-loading="saving"
          @click="confirming = true"
        >
          {{ t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.CONTINUE') }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
  <NucleoWorkOrderConfirmationDialog
    v-else
    :show="show"
    :saving="saving"
    :variant="form.mode"
    @close="confirming = false"
    @confirm="submit"
  />
</template>
